import Box from '@mui/material/Box';
import type {Theme} from '@mui/material/styles';
import {transformHtmlPostToText} from '@perfectpost/perfect-post-common';
import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {transformTextToHtml} from '../../utils/text-transform';

// Bulle d'un commentaire. En sombre, une teinte OPAQUE (le voile `action.hover` posé sur
// `background.paper`) : le bouton « … plus » repose sur la bulle et doit cacher le texte dessous.
export const commentBubbleBg = (theme: Theme) => (theme.palette.mode === 'light' ? '#F2F2F2' : '#2D3135');

/** Un commentaire sous le post, tel que LinkedIn l'affichera. */
export interface PreviewComment {
  key: string;
  /** HTML de l'éditeur, ou texte. */
  content: string;
  /** Délai affiché à la place de l'horodatage LinkedIn : « +10 min ». */
  timeLabel: string;
  fullname?: string;
  picture?: string;
  headline?: string;
  /** Le commentaire est signé par l'auteur du post : la mention « • Vous » de LinkedIn. */
  byPostAuthor?: boolean;
}

/**
 * Un commentaire de l'aperçu : bulle, auteur, délai, « … plus ». Extrait d'`ActivityPreview`,
 * qui n'en affichait qu'un — le premier commentaire legacy — pour afficher TOUS les commentaires
 * programmés, chacun avec son propre dépliage.
 */
export function PreviewCommentItem({
  content,
  timeLabel,
  fullname,
  picture,
  headline,
  byPostAuthor = true,
}: PreviewComment) {
  const {t} = useTranslation();
  const [showMoreComment, toggleShowMoreComment] = useState(true);
  const previewHtml = useMemo(() => transformTextToHtml(transformHtmlPostToText(content)), [content]);

  return (
    <>
      <Box
        component="article"
        className="comments-comment-item"
        sx={{
          paddingBottom: 0,
          margin: '0 16px 12px',
          backgroundColor: 'transparent',
          border: 0,
          clear: 'left',
          minHeight: 0,
          display: 'block',
          position: 'relative',
        }}
      >
        <Box
          className="commments-post-meta"
          sx={{padding: 0, margin: 0, display: 'flex', flexWrap: 'no-wrap', alignItems: 'center'}}
        >
          <Box sx={{alignSelf: 'flex-start', flexShrink: 0, marginTop: '5px'}}>
            <Box
              component="img"
              sx={{
                objectFix: 'contain',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                objectPosition: 'center',
                backgroundPosition: '50%',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
              }}
              src={picture}
              loading="lazy"
            />
          </Box>
          <Box
            className="comments-post-meta__profile-info-wrapper"
            sx={{
              padding: '8px 64px 8px 12px',
              marginLeft: '4px',
              background: commentBubbleBg,
              borderRadius: '0 8px 0 0',
              minHeight: '32px',
              flexGrow: 1,
              flexBasis: 0,
              overflow: 'hidden',
            }}
          >
            <Box sx={{display: 'inline-flex'}}>
              <Box
                component="span"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '14px',
                  fontWeight: 600,
                  lineHeight: '20px',
                }}
              >
                {fullname}
              </Box>
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  verticalAlign: 'top',
                  whiteSpace: 'nowrap',
                  color: (theme) =>
                    theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                {byPostAuthor && (
                  <>
                    <span style={{whiteSpace: 'pre'}}> </span>• {t('activitypreview.you')}
                  </>
                )}
              </Box>
            </Box>
            <Box
              component="span"
              className="comments-post-meta__headline"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '16px',
                color: (theme) => (theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'),
              }}
            >
              <span>{headline}</span>
            </Box>
          </Box>
        </Box>
        <Box
          className="comments-comment-item__options"
          sx={{
            alignItems: 'center',
            display: 'flex',
            float: 'right',
            justifyContent: 'flex-end',
            minWidth: '68px',
            position: 'absolute',
            top: '8px',
            right: '24px',
          }}
        >
          <Box
            component="time"
            sx={{margin: 0, marginRight: '4px', padding: 0, border: 0, fontSize: '12px', lineHeight: '16px'}}
          >
            {timeLabel}
          </Box>
        </Box>
        <Box
          className="comments-comment-item-content-body"
          sx={{
            marginLeft: '44px',
            padding: '0 16px 12px 12px',
            borderRadius: '0 0 8px 8px',
            background: commentBubbleBg,
            wordBreak: 'break-word',
            wordWrap: 'break-word',
          }}
        >
          <Box
            className="feed-shared-inline-show-more-text"
            sx={[
              {
                display: 'block',
                fontSize: '14px',
                lineHeight: '20px',
                maxHeight: '60px',
                overflow: 'hidden',
                margin: 0,
                padding: 0,
                position: 'relative',
                maxWidth: '928px',
              },
              showMoreComment === false && {
                maxHeight: 'none',
                display: 'block',
              },
            ]}
          >
            <Box
              className="comments-comment-item__main-content"
              component="span"
              sx={{
                color: 'inherit',
                lineHeight: 'inherit',
                fontFamily:
                  '-apple-system, system-ui, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Fira Sans, Ubuntu, Oxygen, Oxygen Sans, Cantarell, Droid Sans, Apple Color Emoji, Segoe UI Emoji, Segoe UI Emoji, Segoe UI Symbol, Lucida Grande, Helvetica, Arial, sans-serif',
              }}
              dangerouslySetInnerHTML={{__html: previewHtml}}
            ></Box>
            {showMoreComment && (
              <Box
                component="button"
                role="button"
                sx={{
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  backgroundColor: commentBubbleBg,
                  lineHeight: 'inherit',
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  left: 401,
                  marginBottom: 0,
                  border: 'none',
                  padding: 0,
                  paddingLeft: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: (theme) =>
                    theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                  fontFamily:
                    '-apple-system, system-ui, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Fira Sans, Ubuntu, Oxygen, Oxygen Sans, Cantarell, Droid Sans, Apple Color Emoji, Segoe UI Emoji, Segoe UI Emoji, Segoe UI Symbol, Lucida Grande, Helvetica, Arial, sans-serif',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#0a66c2',
                  },
                }}
                onClick={() => toggleShowMoreComment((b) => !b)}
                type="button"
              >
                <Box component="span" sx={{display: 'inline-block'}}>
                  {t('activitypreview.seeMore.more')}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {showMoreComment === false && (
        <Box
          component="button"
          role="button"
          sx={{
            float: 'right',
            margin: '0 32px 16px 0',
            textAlign: 'left',
            whiteSpace: 'nowrap',
            lineHeight: 'inherit',
            border: 'none',
            padding: 0,
            paddingLeft: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            color: (theme) => (theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'),
            backgroundColor: 'transparent',
            fontFamily:
              '-apple-system, system-ui, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Fira Sans, Ubuntu, Oxygen, Oxygen Sans, Cantarell, Droid Sans, Apple Color Emoji, Segoe UI Emoji, Segoe UI Emoji, Segoe UI Symbol, Lucida Grande, Helvetica, Arial, sans-serif',
            '&:hover': {
              textDecoration: 'underline',
              color: '#0a66c2',
            },
          }}
          onClick={() => toggleShowMoreComment((b) => !b)}
          type="button"
        >
          <Box component="span" sx={{display: 'inline-block'}}>
            {t('activitypreview.seeMore.less')}
          </Box>
        </Box>
      )}
    </>
  );
}
